import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Copy,
  QrCode,
  ExternalLink,
  Trash2,
  MousePointerClick,
  Calendar,
  Globe,
  TrendingUp,
  Users,
  Activity,
  UserPlus,
  Pencil,
  Link2,
  Layers,
  Crown,
  Loader2,
  FlaskConical,
  Check,
  Tag,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard, formatNumber, formatDate, getUTCDateString } from '@/client/lib/utils';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { useFeatureGate, TierBadge } from '@/client/components/feature-gate';
import { ExportButton } from '@/client/components/export-button';
import { LinkPreviewEditor } from '@/client/components/link-preview-editor';
import { EditLinkModal } from '@/client/components/modals';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardTitle, CardDescription } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Input } from '@/client/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

// =============================================================================
// Test Link Section Component
// =============================================================================

function TestLinkSection({ shortUrl, testClicks }: { shortUrl: string; testClicks: number }) {
  const [copied, setCopied] = useState(false);
  const testUrl = `${shortUrl}?_test=1`;

  const handleCopyTestLink = async () => {
    await copyToClipboard(testUrl);
    setCopied(true);
    toast.success('Test link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="rounded-2xl py-0">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="size-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center flex-shrink-0">
            <FlaskConical className="size-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 mb-2">
              <div>
                <CardTitle className="text-base">Test This Link</CardTitle>
                <CardDescription>
                  Share with your team for testing. Clicks won&apos;t affect your real metrics.
                </CardDescription>
              </div>
              {testClicks > 0 && (
                <Badge variant="secondary" className="gap-1 flex-shrink-0">
                  <FlaskConical className="size-3" />
                  {formatNumber(testClicks)} test click{testClicks !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="flex-1 px-3 py-2 rounded-lg border bg-muted/30 font-mono text-sm truncate">
                {testUrl}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyTestLink}
                className="rounded-lg flex-shrink-0 gap-1.5"
              >
                {copied ? (
                  <>
                    <Check className="size-4 text-emerald-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="size-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

export function LinkDetailPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const { openQrModal, openDeleteConfirmation, openShareModal, openUrlChangeConfirmation } =
    useUIStore();
  const queryClient = useQueryClient();

  // Feature gate for project assignment
  const {
    hasAccess: hasProjectAccess,
    requiredTier: projectRequiredTier,
    openUpgradeModal: openProjectUpgradeModal,
    UpgradeModalElement: ProjectUpgradeModal,
  } = useFeatureGate('project_limits', 'Organize links into projects for better management');

  // Destination URL editing state
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [newDestinationUrl, setNewDestinationUrl] = useState('');

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Early return if shortCode is not available
  const validShortCode = shortCode ?? '';

  const { data: link, isLoading } = useQuery({
    queryKey: ['links', validShortCode],
    queryFn: async () => {
      const response = await apiFetch(`/api/links/${validShortCode}`);
      if (!response.ok) throw new Error('Link not found');
      return response.json();
    },
    enabled: !!validShortCode,
    refetchInterval: 5000,
    refetchIntervalInBackground: false, // Pause when tab is backgrounded
  });

  const { data: analyticsSummary } = useQuery({
    queryKey: ['analytics', 'resource', 'link', link?.id, 'summary'],
    queryFn: async () => {
      if (!link?.id) return null;
      // Fetch 30d summary
      const response = await apiFetch(`/api/analytics/resource/link/${link.id}?period=30d`);
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!link?.id,
  });

  // Fetch projects for Plus users
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
    enabled: hasProjectAccess,
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Mutation to update link project
  const updateProjectMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      const response = await apiFetch(`/api/links/${validShortCode}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update project');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links', validShortCode] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Link project updated!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleCopyLink = async () => {
    if (link?.shortUrl) {
      await copyToClipboard(link.shortUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!link) {
    return (
      <div className="text-center py-16">
        <div className="size-20 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
          <Globe className="size-10 text-muted-foreground/50" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Link not found</h2>
        <p className="text-muted-foreground mb-6">
          This link may have been deleted or doesn&apos;t exist.
        </p>
        <Button asChild className="rounded-xl">
          <Link to="/links">
            <ArrowLeft className="size-4" />
            Back to links
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 stagger-children">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="outline" size="icon" asChild className="rounded-xl mt-1">
          <Link to="/links">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          {/* Title or Short URL as main heading */}
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight truncate">
              {link.title || `arml.ink/${shortCode}`}
            </h1>
            <Badge variant={link.isActive ? 'success-subtle' : 'secondary'}>
              {link.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {/* Short URL (shown separately when title exists) */}
          {link.title && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-primary">arml.ink/{shortCode}</span>
              <button
                onClick={handleCopyLink}
                className="p-1 rounded hover:bg-muted transition-colors"
                title="Copy short link"
              >
                <Copy className="size-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
          )}

          {/* Tags */}
          {link.tags && link.tags.length > 0 && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Tag className="size-3.5 text-muted-foreground" />
              {link.tags.map((tag: string) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Destination URL */}
          <p className="text-muted-foreground text-sm truncate max-w-2xl">{link.destinationUrl}</p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsEditModalOpen(true)}
                className="rounded-xl"
              >
                <Pencil className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit link</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" onClick={handleCopyLink} className="rounded-xl">
                <Copy className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy link</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => openQrModal(validShortCode, link.shortUrl)}
                className="rounded-xl"
              >
                <QrCode className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>QR Code</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" asChild className="rounded-xl">
                <a href={link.shortUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-5" />
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open link</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() =>
                  openShareModal({
                    id: link.id,
                    shortCode: validShortCode,
                    title: link.title,
                    contextType: 'link',
                  })
                }
                className="rounded-xl hover:bg-primary/10 hover:border-primary hover:text-primary"
              >
                <UserPlus className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Share analytics</TooltipContent>
          </Tooltip>
          <ExportButton
            link={{
              shortCode: validShortCode,
              shortUrl: link.shortUrl,
              destinationUrl: link.destinationUrl,
              title: link.title,
              totalClicks: link.totalClicks ?? 0,
              createdAt: link.createdAt,
            }}
            dailyStats={[]}
            startDate={getUTCDateString(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))} // Default 30d
            endDate={getUTCDateString(new Date())}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => openDeleteConfirmation('link', validShortCode, validShortCode)}
                className="rounded-xl hover:bg-destructive/10 hover:border-destructive hover:text-destructive"
              >
                <Trash2 className="size-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete link</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card className="rounded-2xl group card-hover py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform">
              <MousePointerClick className="size-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Clicks</p>
              <p className="text-3xl font-bold tabular-nums">
                {formatNumber(link.totalClicks ?? 0)}
              </p>
              {(link.testClicks ?? 0) > 0 && (
                <p className="text-xs text-muted-foreground tabular-nums">
                  + {formatNumber(link.testClicks)} test
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* QR Scans Card */}
        {(() => {
          const totalQrScans = analyticsSummary?.summary?.qrScans ?? link.totalQrScans ?? 0;
          const qrPercentage =
            link.totalClicks > 0 ? Math.round((totalQrScans / link.totalClicks) * 100) : 0;
          return (
            <Card className="rounded-2xl group card-hover py-0">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="size-12 rounded-xl bg-gradient-to-br from-[hsl(280,70%,50%)]/20 to-[hsl(280,70%,50%)]/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <QrCode className="size-6 text-[hsl(280,70%,50%)]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">QR Scans</p>
                  <p className="text-3xl font-bold tabular-nums">{formatNumber(totalQrScans)}</p>
                  {qrPercentage > 0 && (
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {qrPercentage}% of total
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <Card className="rounded-2xl group card-hover py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center group-hover:scale-110 transition-transform">
              <TrendingUp className="size-6 text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="text-xl font-bold">{link.isActive ? 'Active' : 'Inactive'}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl group card-hover py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-[hsl(262,83%,58%)]/20 to-[hsl(262,83%,58%)]/5 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Calendar className="size-6 text-[hsl(262,83%,58%)]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="text-lg font-bold">{formatDate(link.createdAt)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl group card-hover py-0">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="size-12 rounded-xl bg-gradient-to-br from-[hsl(45,93%,58%)]/20 to-[hsl(45,93%,58%)]/5 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Users className="size-6 text-[hsl(45,93%,58%)]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Unique Visitors</p>
              <p className="text-3xl font-bold">
                {formatNumber(analyticsSummary?.summary?.uniqueVisitors ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Destination URL Editor */}
      <Card className="rounded-2xl py-0">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Link2 className="size-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Destination URL</CardTitle>
                <CardDescription>Where this short link redirects to</CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isEditingUrl) {
                  setIsEditingUrl(false);
                  setNewDestinationUrl('');
                } else {
                  setIsEditingUrl(true);
                  setNewDestinationUrl(link.destinationUrl);
                }
              }}
              className="rounded-lg"
            >
              <Pencil className="size-4" />
              {isEditingUrl ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          {isEditingUrl ? (
            <div className="space-y-3">
              <Input
                type="url"
                value={newDestinationUrl}
                onChange={(e) => setNewDestinationUrl(e.target.value)}
                placeholder="https://example.com/new-destination"
                className="font-mono text-sm rounded-xl"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditingUrl(false);
                    setNewDestinationUrl('');
                  }}
                  className="rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (newDestinationUrl && newDestinationUrl !== link.destinationUrl) {
                      openUrlChangeConfirmation(
                        validShortCode,
                        link.destinationUrl,
                        newDestinationUrl
                      );
                      setIsEditingUrl(false);
                      setNewDestinationUrl('');
                    }
                  }}
                  disabled={!newDestinationUrl || newDestinationUrl === link.destinationUrl}
                  className="rounded-lg"
                >
                  Update URL
                </Button>
              </div>
            </div>
          ) : (
            <a
              href={link.destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-primary hover:underline break-all font-mono text-sm"
            >
              {link.destinationUrl}
            </a>
          )}
        </CardContent>
      </Card>

      {/* Link Preview Editor */}
      <LinkPreviewEditor
        shortCode={validShortCode}
        linkTitle={link.title}
        destinationUrl={link.destinationUrl}
        currentPreview={link.meta?.preview}
      />

      {/* Project Selector - Plus Feature */}
      <Card className="rounded-2xl py-0">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Layers className="size-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Project
                  {!hasProjectAccess && <TierBadge tier={projectRequiredTier} size="xs" />}
                </CardTitle>
                <CardDescription>Organize this link into a project</CardDescription>
              </div>
            </div>
            {hasProjectAccess ? (
              <div className="flex items-center gap-2">
                {updateProjectMutation.isPending ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/50">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Updating…</span>
                  </div>
                ) : projectsLoading ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/50">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading projects…</span>
                  </div>
                ) : (
                  <select
                    value={link.projectId || ''}
                    onChange={(e) => updateProjectMutation.mutate(e.target.value || null)}
                    className="px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[180px]"
                  >
                    <option value="">No project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                )}
                {link.projectId && (
                  <Button variant="outline" size="sm" asChild className="rounded-lg">
                    <Link to={`/projects/${link.projectId}`}>View Project</Link>
                  </Button>
                )}
              </div>
            ) : (
              <Button variant="link" onClick={openProjectUpgradeModal}>
                <Crown className="size-4" />
                Upgrade to unlock
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Project Upgrade Modal */}
      {ProjectUpgradeModal}

      {/* Test This Link */}
      <TestLinkSection shortUrl={link.shortUrl} testClicks={link.testClicks ?? 0} />

      {/* Analytics CTA */}
      <Card className="rounded-2xl border-dashed bg-muted/30 py-0">
        <CardContent className="p-8 text-center">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center mx-auto mb-4">
            <Activity className="size-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Detailed Analytics</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            View comprehensive performance data, including timeline, geographic distribution, and
            device breakdown in the Analytics Hub.
          </p>
          <Button asChild className="rounded-xl" size="lg">
            <Link to={`/analytics?resourceTypes=link&resourceIds=${link.id}`}>
              View Full Analytics
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Edit Link Modal */}
      <EditLinkModal
        link={
          link
            ? {
                id: link.id,
                shortCode: link.shortCode,
                shortUrl: link.shortUrl,
                destinationUrl: link.destinationUrl,
                title: link.title,
                tags: link.tags,
                projectId: link.projectId,
                validUntil: link.validUntil,
                maxVisits: link.maxVisits,
                isActive: link.isActive,
              }
            : null
        }
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </div>
  );
}
